import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { FormTextArea } from "../../../../components/form-components/form-textarea.components";
import { Separator } from "../../../../components/ui/separator";
import { useCallback, useEffect, useState } from "react";
import Line from "../../../../assets/svgs/common/line.svg";
import { Switch } from "../../../../components/ui/switch";
import { FormSelect } from "../../../../components/form-components/form-select.components";
import { SocialGenderOptions } from "../../campaign-form.admin.partials";
import { FormMultiSelectLocation } from "../../../../components/form-components/form-multiselect-location.components";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import { useAutocompleteCityCommonHttp } from "../../../../hooks/http/common/utils/google-maps/auto-complete-city.common.http";
import {
	debounce,
	findErrorMessage,
	handleHttpError,
} from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import type { ILocationMinimal } from "../../../../types/models/location.models.types";
import { SocialGender } from "../../../../types/models/social.models.types";
import { useAddNewFavoriteCollectionAdminHttp } from "../../../../hooks/http/admin/favorites/add-new-favorite-collection.admin.http";
import { useNavigate } from "react-router-dom";
import { useAddNewFavoriteWithFiltersAdminHttp } from "../../../../hooks/http/admin/favorites/add-new-favorite-with-filters.admin.http";

const FormSchema = z.object({
	name: z.string().min(1, "Please provide name of group"),
	description: z.string(),
	filter: z
		.object({
			minimumFollowers: z.coerce.number().nullable().default(null),
			maximumFollowers: z.coerce.number().nullable().default(null),
			minimumCampaigns: z.coerce.number().nullable().default(null),
			maximumCampaigns: z.coerce.number().nullable().default(null),
			genderPreference: z.nativeEnum(SocialGender).nullable().default(null),
			engagementRate: z.coerce.number().nullable().default(null),
			locationPreference: z
				.array(
					z.object({
						placeId: z.string(),
						address: z.string(),
					}),
				)
				.default([]),
		})
		.default({}),
});

export const NewFavoritesModalAdminPartial: React.FunctionComponent = () => {
	const [formStage, setFormStage] = useState({
		stage1: { open: true, completed: false },
		stage2: { open: false, completed: false },
	});
	const [predictions, setPredictions] = useState<ILocationMinimal[]>([]);
	const [enableFavorites, setEnableFavorites] = useState(false);

	const { execute: getLocationHttp, fetching: fetchingLocation } =
		useAutocompleteCityCommonHttp();
	const {
		execute: addNewFavoriteCollectionHttp,
		fetching: fetchingAddNewFavoriteCollection,
	} = useAddNewFavoriteCollectionAdminHttp();

	const {
		execute: addNewFavoriteWithFiltersHttp,
		fetching: fetchingAddNewFavoriteWithFilters,
	} = useAddNewFavoriteWithFiltersAdminHttp();

	const { toast } = useToast();
	const navigate = useNavigate();
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			name: "",
			description: "",
		},
	});

	function handleProceed() {
		setFormStage({
			...formStage,
			stage1: { open: false, completed: true },
			stage2: { open: true, completed: false },
		});
	}

	const debouncedLocationSearch = useCallback(
		debounce(async (searchValue: string) => {
			const searchString = searchValue.trim();
			if (!searchString) {
				return;
			}

			const response = await getLocationHttp({
				body: null,
				queryParams: {
					query: searchString,
				},
			});
			if (!response.success) {
				return handleHttpError(response, toast);
			}

			setPredictions(response.data.predictions);
		}, 500),
		[],
	);

	async function handleLocationChange(searchQuery: string) {
		debouncedLocationSearch(searchQuery);
	}

	// function handleBack() {
	// 	setFormStage({
	// 		...formStage,
	// 		stage1: { open: true, completed: false },
	// 		stage2: { open: false, completed: false },
	// 	});
	// }

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.formState.errors) {
			const key = Object.keys(form.formState.errors)[0];

			if (key) {
				const error =
					form.formState.errors[key as keyof typeof form.formState.errors];
				const message = findErrorMessage(error) || "An error occurred";

				toast({
					content: message,
					toastType: "error",
				});
			}
		}
	}, [form.formState.errors, form.resetField]);

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		const response = await addNewFavoriteCollectionHttp({
			body: {
				name: data.name,
				description: data.description,
				socials: [],
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}

		if (
			enableFavorites &&
			!data.filter.minimumFollowers &&
			!data.filter.maximumFollowers &&
			!data.filter.minimumCampaigns &&
			!data.filter.maximumCampaigns &&
			!data.filter.genderPreference &&
			!data.filter.engagementRate &&
			!data.filter.locationPreference.length
		) {
			toast({
				content: "Please fill in at least one filter",
				toastType: "error",
			});
			return;
		}
		if (enableFavorites) {
			const responseWithFilters = await addNewFavoriteWithFiltersHttp({
				body: {
					favouriteSocial: response.data.favouriteSocial.id,
					filter: {
						minFollowers: data.filter.minimumFollowers,
						maxFollowers: data.filter.maximumFollowers,
						minParticipatedCampaigns: data.filter.minimumCampaigns,
						maxParticipatedCampaigns: data.filter.maximumCampaigns,
						gender: data.filter.genderPreference,
						engagement: data.filter.engagementRate,
						locationPreference: data.filter.locationPreference.map(
							(location) => location.placeId,
						),
						platform: "instagram",
					},
				},
			});

			if (!responseWithFilters.success) {
				return handleHttpError(responseWithFilters, toast);
			}
		}

		toast({
			content: "Favorite group created successfully",
			toastType: "success",
		});
		navigate("/admin/favorites");
		document.getElementById("getAllFavorites")?.click();
		return;
	}

	return (
		<DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
			<DialogHeader className="w-full">
				<div className="flex flex-col">
					<p className="flex gap-1 font-semibold text-28 leading-8">
						New favourite group
					</p>
					<span className="text-secondary">
						Specific creators for specific campaigns
					</span>
				</div>
			</DialogHeader>
			<div className="flex justify-evenly items-center py-5 w-full font-semibold">
				<div
					className={`flex flex-col text-left ${formStage.stage1.open || formStage.stage1.completed ? "text-primary" : "text-tertiary"}`}
				>
					<span className="text-tertiary text-xs">Step 01</span>
					<p>Group name</p>
				</div>
				<Line />
				<div
					className={`flex flex-col text-left ${formStage.stage2.open ? "text-primary" : "text-tertiary"}`}
				>
					<span className="text-tertiary text-xs">Step 02</span>
					<p>Favorite filters (Optional)</p>
				</div>
			</div>
			<Separator />
			<div className="flex items-center pt-2 w-full">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="relative flex flex-col gap-4 w-full"
					>
						{formStage.stage1.open && (
							<div className="relative flex flex-col gap-4 w-full">
								<FormInput
									form={form}
									name="name"
									label="What do you want to call this favorite group?"
									placeholder="Enter group name"
									required
									type="text"
								/>

								<FormTextArea
									form={form}
									name="description"
									label=""
									placeholder="Write a short description for this favorite group"
								/>
							</div>
						)}

						{formStage.stage2.open && (
							<div className="flex flex-col items-center gap-6 p-5 border border-border rounded-lg w-full font-semibold">
								<div className="flex justify-between items-center w-full">
									<p>Apply filters</p>
									<Switch
										checked={enableFavorites}
										onClick={() => {
											setEnableFavorites(!enableFavorites);
										}}
										className="relative"
									/>
								</div>
								{enableFavorites && (
									<div className="flex flex-col gap-8 w-full h-64">
										<Separator />
										<ScrollArea className="pr-4 w-full">
											<div className="gap-5 grid grid-cols-2 w-full">
												<FormInput
													form={form}
													name="filter.minimumFollowers"
													label="Minimum followers"
													placeholder="Enter minimum followers"
													type="number"
												/>
												<FormInput
													form={form}
													name="filter.maximumFollowers"
													label="Maximum followers"
													placeholder="Enter maximum followers"
													type="number"
												/>
												<FormInput
													form={form}
													name="filter.minimumCampaigns"
													label="Minimum campaigns"
													placeholder="Enter minimum campaigns"
													type="number"
												/>
												<FormInput
													form={form}
													name="filter.maximumCampaigns"
													label="Maximum campaigns"
													placeholder="Enter maximum campaigns"
													type="number"
												/>

												<FormSelect
													form={form}
													name="filter.genderPreference"
													label="Gender preference"
													placeholder="Select preference"
													options={Object.values(SocialGenderOptions)}
												/>

												<FormInput
													form={form}
													name="filter.engagementRate"
													label="Engagement rate"
													placeholder="Enter engagement rate"
													type="number"
												/>
												<div className="col-span-2">
													<FormMultiSelectLocation
														form={form}
														name="filter.locationPreference"
														label="Location"
														placeholder="Select location"
														fetcher={handleLocationChange}
														fetching={fetchingLocation}
														options={predictions.map((prediction) => ({
															placeId: prediction.placeId,
															address: prediction.address,
														}))}
													/>
												</div>
											</div>
										</ScrollArea>
									</div>
								)}
							</div>
						)}

						{formStage.stage1.open && (
							<div className="flex justify-end w-full">
								<Button
									type={form.watch("name") ? "button" : "submit"}
									onClick={() => {
										form.watch("name") && handleProceed();
									}}
									className="border-approvebuttonborder bg-approvebuttonbg px-12 py-6 border w-fit font-semibold text-approvebuttontext tracking-wider"
								>
									Next
								</Button>
							</div>
						)}
						{formStage.stage2.open && (
							<div className="flex justify-end gap-5 w-full">
								<Button
									disabled={
										fetchingAddNewFavoriteCollection ||
										fetchingAddNewFavoriteWithFilters
									}
									onClick={() => {
										form.handleSubmit(onSubmit);
									}}
									className="border-approvebuttonborder bg-approvebuttonbg px-12 py-6 border w-fit font-semibold text-approvebuttontext tracking-wider"
								>
									Finish
								</Button>
							</div>
						)}
					</form>
				</Form>
			</div>
		</DialogContent>
	);
};
